import { useState, useEffect, useRef } from "react";
import { AppProvider, useApp } from "./context/AppContext";
import { useTheme } from "./context/ThemeContext";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Patients from "./pages/Patients";
import Settings from "./pages/Settings";
import Imaging from "./pages/Imaging";
import SystemStatus from "./pages/SystemStatus";
import LabReports from "./pages/LabReports";
import ClinicalReview from "./pages/ClinicalReview";
import WorkQueue     from "./pages/WorkQueue";
import "./index.css";

const ROLE_PAGES = {
  admin:        ["dashboard","patients","work-queue","reports","imaging","clinical-review","settings","system"],
  doctor:       ["dashboard","patients","work-queue","reports","imaging","clinical-review","settings"],
  technician:   ["dashboard","patients","work-queue","reports","imaging","clinical-review"],
  receptionist: ["dashboard","patients","work-queue","reports","imaging"],
};

// ── PIN Lock Overlay ──────────────────────────────────────────────────────────
function PinLockOverlay() {
  const { user, unlockWithPin, logout } = useApp();
  const [pin,       setPin]       = useState("");
  const [error,     setError]     = useState("");
  const [attempts,  setAttempts]  = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [showForgot,setShowForgot]= useState(false);
  const [forgotEmail,   setForgotEmail]   = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg,     setForgotMsg]     = useState("");
  const [forgotErr,     setForgotErr]     = useState("");

  const BASE = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app");

  const handlePinDigit = async (digit) => {
    if (loading) return;
    const newPin = pin + digit;
    setPin(newPin);
    setError("");

    if (newPin.length === 4) {
      setLoading(true);
      const res = await unlockWithPin(newPin);
      setLoading(false);
      if (res.success) {
        setPin("");
        return;
      }
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setPin("");
      if (newAttempts >= 3) {
        // 3 wrong attempts → full logout
        logout();
        return;
      }
      setError(`Wrong PIN. ${3 - newAttempts} attempt${3 - newAttempts !== 1 ? "s" : ""} remaining.`);
    }
  };

  const handleSendResetEmail = async () => {
    setForgotLoading(true); setForgotErr(""); setForgotMsg("");
    try {
      const res = await fetch(`${BASE}/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      if (res.ok) setForgotMsg(data.message || "Reset link sent.");
      else setForgotErr(data.detail || "Failed to send.");
    } catch { setForgotErr("Network error."); }
    finally { setForgotLoading(false); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.92)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999, backdropFilter: "blur(6px)",
    }}>
      <div style={{ background: "#fff", borderRadius: 20, padding: "36px 32px", width: 340, textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>

        {!showForgot ? (
          <>
            {/* Avatar */}
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#EEF2FF", color: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 800, margin: "0 auto 16px" }}>
              {user?.username?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#0F172A", marginBottom: 4 }}>Session Locked</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 24 }}>
              {user?.username} · Enter your PIN to continue
            </div>

            {/* PIN dots */}
            <div style={{ display: "flex", justifyContent: "center", gap: 14, marginBottom: 24 }}>
              {[0,1,2,3].map(i => (
                <div key={i} style={{
                  width: 16, height: 16, borderRadius: "50%",
                  background: i < pin.length ? "#4F46E5" : "#E4E8F0",
                  transition: "background 0.15s",
                }} />
              ))}
            </div>

            {error && <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 14, fontWeight: 500 }}>{error}</div>}

            {/* Numpad */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
              {["1","2","3","4","5","6","7","8","9","","0","⌫"].map((d, i) => (
                <button key={i} onClick={() => {
                  if (d === "⌫") { setPin(p => p.slice(0,-1)); setError(""); }
                  else if (d !== "") handlePinDigit(d);
                }} style={{
                  height: 52, borderRadius: 10, border: "1px solid #E4E8F0",
                  background: d === "" ? "transparent" : "#F8FAFC",
                  fontSize: d === "⌫" ? 18 : 20, fontWeight: 600, color: "#0F172A",
                  cursor: d === "" ? "default" : "pointer",
                  transition: "all 0.1s",
                  opacity: loading ? 0.6 : 1,
                }}
                  onMouseEnter={e => { if (d !== "") e.currentTarget.style.background = "#EEF2FF"; }}
                  onMouseLeave={e => { if (d !== "") e.currentTarget.style.background = "#F8FAFC"; }}
                  disabled={loading}
                >{loading && d !== "" && pin.length === 4 ? "…" : d}</button>
              ))}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button onClick={() => setShowForgot(true)}
                style={{ background: "none", border: "none", color: "#64748B", fontSize: 12, cursor: "pointer" }}>
                Forgot PIN?
              </button>
              <button onClick={logout}
                style={{ background: "none", border: "none", color: "#DC2626", fontSize: 12, cursor: "pointer" }}>
                Sign out
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#0F172A", marginBottom: 8 }}>Reset PIN via Email</div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 20 }}>
              A password reset link will be sent to the admin recovery email. After resetting your password, your PIN will also be cleared.
            </div>
            {forgotErr && <div className="alert alert-red" style={{ marginBottom: 12, textAlign: "left" }}>{forgotErr}</div>}
            {forgotMsg ? (
              <div className="alert alert-green" style={{ marginBottom: 16, textAlign: "left" }}>{forgotMsg}</div>
            ) : (
              <div className="form-group" style={{ textAlign: "left", marginBottom: 16 }}>
                <label className="form-label">Recovery Email</label>
                <input className="input" type="email" value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)} placeholder="admin@example.com" autoFocus />
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => { setShowForgot(false); setForgotEmail(""); setForgotMsg(""); setForgotErr(""); }}>Back</button>
              {!forgotMsg && (
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={forgotLoading || !forgotEmail} onClick={handleSendResetEmail}>
                  {forgotLoading ? "Sending..." : "Send Link"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Maintenance page ──────────────────────────────────────────────────────────
function MaintenancePage() {
  const { login, loginMfa } = useApp();
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password
  const [showForgot,    setShowForgot]    = useState(false);
  const [forgotMode,    setForgotMode]    = useState(null);
  const [forgotEmail,   setForgotEmail]   = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg,     setForgotMsg]     = useState("");
  const [forgotErr,     setForgotErr]     = useState("");
  const [mfaResetCode,    setMfaResetCode]    = useState("");
  const [mfaResetNew,     setMfaResetNew]     = useState("");
  const [mfaResetCon,     setMfaResetCon]     = useState("");
  const [mfaResetLoading, setMfaResetLoading] = useState(false);
  const [mfaResetMsg,     setMfaResetMsg]     = useState("");
  const [mfaResetErr,     setMfaResetErr]     = useState("");

  const [mfaStep,      setMfaStep]      = useState(false);
  const [mfaTempToken, setMfaTempToken] = useState(null);
  const [mfaCode,      setMfaCode]      = useState("");
  const [mfaLoading,   setMfaLoading]   = useState(false);
  const [mfaError,     setMfaError]     = useState("");

  const BASE = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app");

  const handleAdminLogin = async () => {
    setError(""); setLoading(true);
    const res = await login(username, password);
    setLoading(false);
    if (res.mfaRequired) {
      setMfaTempToken(res.tempToken);
      setMfaStep(true);
      setMfaCode(""); setMfaError("");
      return;
    }
    if (!res.success) setError(res.error || "Invalid credentials");
  };

  const handleMfaSubmit = async () => {
    setMfaError(""); setMfaLoading(true);
    const res = await loginMfa(mfaTempToken, mfaCode.trim());
    setMfaLoading(false);
    if (!res.success) { setMfaError(res.error || "Invalid code."); return; }
  };

  const handleForgotEmail = async () => {
    setForgotLoading(true); setForgotErr(""); setForgotMsg("");
    try {
      const res = await fetch(`${BASE}/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      if (res.ok) setForgotMsg(data.message || "Reset link sent.");
      else setForgotErr(data.detail || "Failed to send.");
    } catch { setForgotErr("Network error."); }
    finally { setForgotLoading(false); }
  };

  const handleMfaReset = async () => {
    setMfaResetErr(""); setMfaResetMsg("");
    if (!mfaResetCode) return setMfaResetErr("Enter your authenticator code or a backup code.");
    if (!mfaResetNew) return setMfaResetErr("New password cannot be empty.");
    if (mfaResetNew.length < 8) return setMfaResetErr("Password must be at least 8 characters.");
    if (mfaResetNew !== mfaResetCon) return setMfaResetErr("Passwords do not match.");
    setMfaResetLoading(true);
    try {
      const res = await fetch(`${BASE}/auth/mfa/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: mfaResetCode.trim(), new_password: mfaResetNew }),
      });
      const data = await res.json();
      if (res.ok) { setMfaResetMsg("Admin password reset. You can now sign in."); setMfaResetCode(""); setMfaResetNew(""); setMfaResetCon(""); }
      else setMfaResetErr(data.detail || "Reset failed.");
    } catch { setMfaResetErr("Network error."); }
    finally { setMfaResetLoading(false); }
  };

  const closeForgot = () => {
    setShowForgot(false); setForgotMode(null);
    setForgotEmail(""); setForgotMsg(""); setForgotErr("");
    setMfaResetCode(""); setMfaResetNew(""); setMfaResetCon(""); setMfaResetMsg(""); setMfaResetErr("");
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0F172A", flexDirection: "column", gap: 20, padding: 24 }}>
      <div style={{ fontSize: 52 }}>🔧</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", textAlign: "center" }}>System Under Maintenance</div>
      <div style={{ fontSize: 15, color: "#94A3B8", textAlign: "center", maxWidth: 420, lineHeight: 1.6 }}>
        We are currently performing scheduled maintenance.<br />The system will be back online shortly.
      </div>
      <div style={{ fontSize: 12, color: "#475569" }}>Nashman Institute EMR — Pathology & Regenerative Medicine</div>

      {!showAdminLogin ? (
        <button onClick={() => setShowAdminLogin(true)} style={{ marginTop: 8, padding: "8px 20px", borderRadius: 8, border: "1px solid #334155", background: "transparent", color: "#64748B", fontSize: 12, cursor: "pointer" }}>
          Administrator Login
        </button>
      ) : (
        <div style={{ background: "#1E293B", borderRadius: 12, padding: 24, width: "100%", maxWidth: 360, border: "1px solid #334155" }}>
          {!mfaStep ? (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 16, textAlign: "center" }}>Admin Access</div>
              {error && <div style={{ background: "#7F1D1D", color: "#FCA5A5", padding: "8px 12px", borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>}
              <input style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #334155", background: "#0F172A", color: "#fff", fontSize: 13, boxSizing: "border-box", marginBottom: 12 }}
                value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" />
              <input type="password" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #334155", background: "#0F172A", color: "#fff", fontSize: 13, boxSizing: "border-box", marginBottom: 16 }}
                value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdminLogin()} placeholder="Password" />
              <button onClick={handleAdminLogin} disabled={loading} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "none", background: "#4F46E5", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                {loading ? "Signing in..." : "Sign In as Admin"}
              </button>
              <button onClick={() => { setShowAdminLogin(false); setError(""); }} style={{ width: "100%", marginTop: 8, padding: "8px", background: "transparent", border: "none", color: "#475569", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <button onClick={() => setShowForgot(true)} style={{ background: "none", border: "none", color: "#6366F1", fontSize: 11, cursor: "pointer" }}>
                  Forgot admin password?
                </button>
              </div>
            </>
          ) : (
            <>
              <button onClick={() => { setMfaStep(false); setMfaTempToken(null); setMfaCode(""); setMfaError(""); }}
                style={{ background: "none", border: "none", color: "#64748B", fontSize: 12, cursor: "pointer", marginBottom: 14, padding: 0 }}>
                ← Back
              </button>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 6, textAlign: "center" }}>🔐 Two-factor verification</div>
              <div style={{ fontSize: 12, color: "#94A3B8", textAlign: "center", marginBottom: 16 }}>Enter the code from your authenticator app</div>
              {mfaError && <div style={{ background: "#7F1D1D", color: "#FCA5A5", padding: "8px 12px", borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{mfaError}</div>}
              <input
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #334155", background: "#0F172A", color: "#fff", fontSize: 22, boxSizing: "border-box", marginBottom: 6, textAlign: "center", fontFamily: "monospace", letterSpacing: 6 }}
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/[^0-9A-Za-z-]/g, "").slice(0, 9).toUpperCase())}
                placeholder="000000" autoFocus
                onKeyDown={e => e.key === "Enter" && handleMfaSubmit()}
              />
              <div style={{ fontSize: 11, color: "#475569", textAlign: "center", marginBottom: 16 }}>Lost your phone? Enter a backup code instead.</div>
              <button onClick={handleMfaSubmit} disabled={mfaLoading || mfaCode.length < 6}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "none", background: "#4F46E5", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                {mfaLoading ? "Verifying..." : "Verify"}
              </button>
            </>
          )}
        </div>
      )}

      {/* Forgot password modal */}
      {showForgot && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 }}>
          <div style={{ background: "#1E293B", borderRadius: 14, width: "100%", maxWidth: 420, border: "1px solid #334155", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #334155" }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>Reset Admin Password</div>
              <button onClick={closeForgot} style={{ background: "none", border: "none", color: "#64748B", fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              {!forgotMode && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <p style={{ fontSize: 13, color: "#94A3B8", marginBottom: 8 }}>How would you like to reset the admin password?</p>
                  {[
                    { mode: "email",  icon: "📧", label: "Reset via Email",        desc: "A reset link will be sent to your recovery email" },
                    { mode: "mfa",    icon: "🔐", label: "Reset via Authenticator", desc: "Enter your 6-digit code or a backup code" },
                  ].map(opt => (
                    <button key={opt.mode} onClick={() => setForgotMode(opt.mode)} style={{ padding: "12px 14px", borderRadius: 8, border: "1px solid #334155", background: "#0F172A", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 22 }}>{opt.icon}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: "#64748B" }}>{opt.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {forgotMode === "email" && (
                <div>
                  <button onClick={() => setForgotMode(null)} style={{ background: "none", border: "none", color: "#64748B", fontSize: 12, cursor: "pointer", marginBottom: 12 }}>← Back</button>
                  {forgotErr && <div style={{ background: "#7F1D1D", color: "#FCA5A5", padding: "8px 12px", borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{forgotErr}</div>}
                  {forgotMsg ? (
                    <div style={{ background: "#052E16", color: "#86EFAC", padding: "10px 12px", borderRadius: 6, fontSize: 13 }}>{forgotMsg}<br/><span style={{ fontSize: 11, color: "#6EE7B7", marginTop: 4, display: "block" }}>Check your inbox. Link expires in 15 minutes.</span></div>
                  ) : (
                    <div>
                      <div style={{ background: "#1E3A5F", color: "#93C5FD", padding: "8px 12px", borderRadius: 6, fontSize: 12, marginBottom: 12 }}>Enter the recovery email saved in Settings → Passwords.</div>
                      <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="admin@example.com" autoFocus
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #334155", background: "#0F172A", color: "#fff", fontSize: 13, boxSizing: "border-box" }} />
                    </div>
                  )}
                </div>
              )}
              {forgotMode === "mfa" && (
                <div>
                  <button onClick={() => setForgotMode(null)} style={{ background: "none", border: "none", color: "#64748B", fontSize: 12, cursor: "pointer", marginBottom: 12 }}>← Back</button>
                  {mfaResetErr && <div style={{ background: "#7F1D1D", color: "#FCA5A5", padding: "8px 12px", borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{mfaResetErr}</div>}
                  {mfaResetMsg ? (
                    <div style={{ background: "#052E16", color: "#86EFAC", padding: "10px 12px", borderRadius: 6, fontSize: 13 }}>{mfaResetMsg}</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>Authenticator code or backup code</div>
                        <input value={mfaResetCode}
                          onChange={e => setMfaResetCode(e.target.value.replace(/[^0-9A-Za-z-]/g, "").slice(0, 9).toUpperCase())}
                          placeholder="000000" autoFocus
                          style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #334155", background: "#0F172A", color: "#fff", fontSize: 18, boxSizing: "border-box", textAlign: "center", fontFamily: "monospace", letterSpacing: 4 }} />
                        <div style={{ fontSize: 10, color: "#475569", marginTop: 4 }}>Lost your phone? Enter a backup code instead.</div>
                      </div>
                      {[
                        { label: "New Admin Password", val: mfaResetNew, set: setMfaResetNew, ph: "Min 8 characters" },
                        { label: "Confirm Password",   val: mfaResetCon, set: setMfaResetCon, ph: "Repeat password" },
                      ].map(f => (
                        <div key={f.label}>
                          <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>{f.label}</div>
                          <input type="password" value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph}
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #334155", background: "#0F172A", color: "#fff", fontSize: 13, boxSizing: "border-box" }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 20px", borderTop: "1px solid #334155" }}>
              <button onClick={closeForgot} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #334155", background: "transparent", color: "#94A3B8", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              {forgotMode === "email" && !forgotMsg && (
                <button onClick={handleForgotEmail} disabled={forgotLoading || !forgotEmail}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#4F46E5", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: (forgotLoading || !forgotEmail) ? 0.6 : 1 }}>
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </button>
              )}
              {forgotMode === "mfa" && !mfaResetMsg && (
                <button onClick={handleMfaReset} disabled={mfaResetLoading || mfaResetCode.length < 6 || !mfaResetNew}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#4F46E5", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: (mfaResetLoading || mfaResetCode.length < 6 || !mfaResetNew) ? 0.6 : 1 }}>
                  {mfaResetLoading ? "Resetting..." : "Reset Password"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main shell ────────────────────────────────────────────────────────────────
function Shell() {
  const { user, backendOk, serverStatus, maintenance, pinLocked } = useApp();
  const [busyToast, setBusyToast] = useState(null);

  useEffect(() => {
    const show = (e) => setBusyToast(e.detail?.message || "Server is busy, retrying...");
    const hide = () => setBusyToast(null);
    window.addEventListener("emr:busy", show);
    window.addEventListener("emr:busy-clear", hide);
    return () => { window.removeEventListener("emr:busy", show); window.removeEventListener("emr:busy-clear", hide); };
  }, []);
  const { t } = useTheme();
  const [active, setActive]                   = useState(() => sessionStorage.getItem("emr_active_page") || "dashboard");
  const [selectedPatient, setSelectedPatient] = useState(() => sessionStorage.getItem("emr_selected_patient") || null);
  const prevUsernameRef = useRef(null);

  // Reset to dashboard when a different user logs in
  useEffect(() => {
    const currentUsername = user?.username || null;
    if (prevUsernameRef.current !== null && prevUsernameRef.current !== currentUsername) {
      setActive("dashboard");
      sessionStorage.removeItem("emr_active_page");
    }
    prevUsernameRef.current = currentUsername;
  }, [user?.username]);

  // Listen for in-app navigation events (e.g. PatientDetail → Imaging)
  useEffect(() => {
    const handler = (e) => {
      const page = e.detail?.page;
      const allowed = ROLE_PAGES[user?.role] || ["dashboard"];
      if (page && allowed.includes(page)) {
        setActive(page);
        sessionStorage.setItem("emr_active_page", page);
        if (page !== "patients") { setSelectedPatient(null); sessionStorage.removeItem("emr_selected_patient"); sessionStorage.removeItem("emr_nav_rep"); }
      }
    };
    window.addEventListener("emr:navigate", handler);
    return () => window.removeEventListener("emr:navigate", handler);
  }, [user?.role]);

  // Handle password reset link
  if (!user) {
    const isReset = window.location.search.includes("token=") ||
                    window.location.pathname === "/reset-password";
    if (isReset) return <ResetPassword />;
  }

  if (maintenance && (!user || user.role !== "admin")) return <MaintenancePage />;
  if (!user) return <Login />;

  const allowed = ROLE_PAGES[user.role] || ["dashboard"];

  const handleSetActive = (page) => {
    if (!allowed.includes(page)) return;
    const prevPage = active;
    setActive(page);
    sessionStorage.setItem("emr_active_page", page);
    if (page !== "patients") {
      // Leaving patients tab — clear selected patient and all pt_pd_* keys
      setSelectedPatient(null);
      sessionStorage.removeItem("emr_selected_patient");
      ["pt_pd_view","pt_pd_rep","pt_pd_type","pt_pd_param"].forEach(k => sessionStorage.removeItem(k));
    } else if (prevPage !== "patients") {
      // Entering patients tab from another tab — clear any patient set by other tabs
      setSelectedPatient(null);
      sessionStorage.removeItem("emr_selected_patient");
      ["pt_pd_view","pt_pd_rep","pt_pd_type","pt_pd_param"].forEach(k => sessionStorage.removeItem(k));
    }
  };

  const handleSetSelectedPatient = (id) => {
    setSelectedPatient(id);
    if (id) sessionStorage.setItem("emr_selected_patient", id);
    else sessionStorage.removeItem("emr_selected_patient");
  };

  const safeActive = allowed.includes(active) ? active : "dashboard";

  return (
    <div className="app-shell">
      {/* PIN lock overlay — shown over the app when idle */}
      {pinLocked && <PinLockOverlay />}

      {/* Busy toast — shows briefly when server pool is exhausted and retrying */}
      {busyToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#1E293B", color: "#F8FAFC", padding: "10px 20px",
          borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 9999,
          display: "flex", alignItems: "center", gap: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          animation: "fadeIn 0.2s ease",
        }}>
          <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #64748B",
            borderTopColor: "#94A3B8", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
          {busyToast}
        </div>
      )}

      <Sidebar active={safeActive} setActive={handleSetActive} />
      <div className="main-content">
        <div className="topbar">
          <div style={{ flex:1, display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#0F172A", textTransform:"capitalize" }}>
              {safeActive === "reports" ? "Lab Register" : safeActive}
            </div>
            {backendOk === false && <span className="badge badge-red">Backend offline</span>}
            {backendOk === true  && <span className="badge badge-green" style={{ fontSize:10 }}>● Live</span>}
          </div>
          <div style={{ fontSize:12, color:"#94A3B8" }}>
            {new Date().toLocaleDateString(t.dateLocale || "en-GB", { weekday:"long", year:"numeric", month:"long", day:"numeric" })}
          </div>
        </div>

        {/* Server status banner */}
        {serverStatus === "down" && (
          <div style={{
            background:"#FEF2F2", borderBottom:"1px solid #FCA5A5",
            padding:"10px 24px", display:"flex", alignItems:"center", gap:10,
            fontSize:13, color:"#991B1B",
          }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#DC2626", flexShrink:0,
              animation:"pulse 1.5s ease-in-out infinite" }} />
            <strong>Server is restarting</strong> — Please wait, the system will be back online shortly. Do not refresh.
            <span style={{ marginLeft:"auto", fontSize:11, color:"#B91C1C" }}>Auto-reconnecting...</span>
          </div>
        )}
        {serverStatus === "slow" && (
          <div style={{
            background:"#FFFBEB", borderBottom:"1px solid #FCD34D",
            padding:"8px 24px", display:"flex", alignItems:"center", gap:10,
            fontSize:13, color:"#92400E",
          }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#F59E0B", flexShrink:0 }} />
            Server response is slow — some pages may take longer to load than usual.
          </div>
        )}
        <div className="page-body">
          {safeActive === "dashboard"        && <Dashboard setActive={handleSetActive} setSelectedPatient={handleSetSelectedPatient} />}
          {safeActive === "patients"         && <Patients selectedPatient={selectedPatient} setSelectedPatient={handleSetSelectedPatient} />}
          {safeActive === "work-queue"       && <WorkQueue />}
          {safeActive === "reports"          && <LabReports />}
          {safeActive === "imaging"          && <Imaging />}
          {safeActive === "clinical-review"  && <ClinicalReview />}
          {safeActive === "settings"         && (user.role === "doctor" || user.role === "admin") && <Settings />}
          {safeActive === "system"           && user.role === "admin" && <SystemStatus />}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}